import { useState, useEffect, useCallback } from "react";
import { Briefcase, Plus, Trash2, Edit, ToggleLeft, ToggleRight, Copy, UserPlus, Mail, Smartphone, Search, BookmarkPlus, FileStack } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { JOB_PREFERENCES } from "@/constants";
import { useJobPreferences } from "@/hooks/useJobPreferences";
import { formatDateFr } from "@/lib/formatters";
import { api } from "./adminApi";

export default function OffersTab({ onNavigateToApplications }) {
  const { prefs: dynamicPrefs } = useJobPreferences();
  const JOB_PREFS = dynamicPrefs.length ? dynamicPrefs : JOB_PREFERENCES;
  const [offers, setOffers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [assignOffer, setAssignOffer] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [searchCand, setSearchCand] = useState("");
  const [optsEmail, setOptsEmail] = useState(true);
  const [optsSms, setOptsSms] = useState(true);
  const [customSms, setCustomSms] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", location: "", hours: "", salary: "", requirements: "", benefits: "", profile_needed: "", categories: [], mission_start: "", mission_end: "", agency_id: "" });
  const [agencies, setAgencies] = useState([]);
  const fetchOffers = useCallback(async () => { try { const r = await api.get("/offers", { params: { scope: "mine" } }); setOffers(r.data); } catch { /* noop */ } }, []);
  const fetchTemplates = useCallback(async () => { try { const r = await api.get("/offers", { params: { is_template: true } }); setTemplates(r.data); } catch { /* noop */ } }, []);
  const fetchAgencies = useCallback(async () => { try { const r = await api.get("/agencies"); setAgencies(r.data); } catch { /* noop */ } }, []);
  useEffect(() => { fetchOffers(); fetchTemplates(); fetchAgencies(); }, [fetchOffers, fetchTemplates, fetchAgencies]);

  const handleSaveAsTemplate = async (offer) => {
    const name = window.prompt("Nom du modèle :", offer.title);
    if (!name) return;
    try {
      await api.post(`/offers/${offer.offer_id}/save-as-template`, { template_name: name });
      toast.success(`Modèle "${name}" créé`);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      const r = await api.post(`/offers/from-template/${template.offer_id}`);
      toast.success(`Offre créée depuis le modèle : ${r.data.offer_number} (inactive)`);
      setShowTemplates(false);
      fetchOffers();
      openEdit(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Supprimer le modèle "${template.template_name || template.title}" ?`)) return;
    try {
      await api.delete(`/offers/${template.offer_id}`);
      toast.success("Modèle supprimé");
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openAssign = async (offer) => {
    setAssignOffer(offer);
    setSelectedUsers(new Set((offer.assigned_users || []).map(u => u.user_id)));
    setCustomSms("");
    try {
      const r = await api.get("/candidates");
      setCandidates(r.data);
    } catch { toast.error("Impossible de charger les candidats"); }
  };

  const handleAssign = async () => {
    if (selectedUsers.size === 0) { toast.error("Sélectionnez au moins un candidat"); return; }
    setAssigning(true);
    try {
      const r = await api.post(`/offers/${assignOffer.offer_id}/assign`, {
        user_ids: Array.from(selectedUsers),
        send_email: optsEmail,
        send_sms: optsSms,
        custom_message: customSms,
      });
      const d = r.data;
      const errCount = (d.errors || []).length;
      toast.success(`Affectation : ${d.emails_sent} email(s), ${d.sms_sent} SMS envoyé(s)${d.sms_skipped > 0 ? `, ${d.sms_skipped} SMS ignoré(s)` : ""}${errCount > 0 ? ` — ${errCount} avertissement(s)` : ""}`);
      if (errCount > 0) console.log("Affectation - détails :", d.errors);
      setAssignOffer(null);
      fetchOffers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setAssigning(false); }
  };

  const resetForm = () => setForm({ title: "", description: "", location: "", hours: "", salary: "", requirements: "", benefits: "", profile_needed: "", categories: [], mission_start: "", mission_end: "", agency_id: "" });

  const handleSave = async () => {
    if (!form.title || !form.description) { toast.error("Titre et description requis"); return; }
    try {
      if (editOffer) { await api.put(`/offers/${editOffer.offer_id}`, form); toast.success("Offre modifiée"); }
      else { await api.post("/offers", form); toast.success("Offre créée"); }
      setShowForm(false); setEditOffer(null); resetForm(); fetchOffers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const handleDelete = async (id) => { if (!window.confirm("Supprimer cette offre ?")) return; try { await api.delete(`/offers/${id}`); toast.success("Offre supprimée"); fetchOffers(); } catch { toast.error("Erreur"); } };
  const handleDuplicate = async (offer) => {
    try {
      const r = await api.post(`/offers/${offer.offer_id}/duplicate`);
      toast.success(`Offre dupliquée : ${r.data.offer_number} (inactive, à réviser)`);
      fetchOffers();
      openEdit(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de la duplication"); }
  };
  const handleToggleStatus = async (offer) => { const s = offer.status === "active" ? "inactive" : "active"; try { await api.put(`/offers/${offer.offer_id}`, { status: s }); fetchOffers(); } catch { toast.error("Erreur"); } };
  const openEdit = (offer) => { setEditOffer(offer); setForm({ title: offer.title, description: offer.description, location: offer.location || "", hours: offer.hours || "", salary: offer.salary || "", requirements: offer.requirements || "", benefits: offer.benefits || "", profile_needed: offer.profile_needed || "", categories: offer.categories || [], mission_start: offer.mission_start || "", mission_end: offer.mission_end || "", agency_id: offer.agency_id || "" }); setShowForm(true); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-500">{offers.length} offre(s){templates.length > 0 && <span className="ml-2 text-[#662483] font-semibold">— {templates.length} modèle(s)</span>}</p>
        <div className="flex gap-2">
          {templates.length > 0 && (
            <Button onClick={() => setShowTemplates(true)} variant="outline" className="rounded-none border-[#662483] text-[#662483] font-semibold" data-testid="open-templates-btn">
              <FileStack className="w-4 h-4 mr-2" /> Modèles ({templates.length})
            </Button>
          )}
          <Button onClick={() => { resetForm(); setEditOffer(null); setShowForm(true); }} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="add-offer-btn"><Plus className="w-4 h-4 mr-2" /> Nouvelle offre</Button>
        </div>
      </div>
      <div className="space-y-3">
        {offers.map((o) => (
          <div key={o.offer_id} className="bg-white border border-gray-200 p-4 flex items-center justify-between gap-4" data-testid={`admin-offer-${o.offer_id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1"><span className="text-xs font-mono text-[#662483]">{o.offer_number || ""}</span><h3 className="text-sm font-bold text-[#3F3F3F] truncate">{o.title}</h3><Badge className={`rounded-none text-xs ${o.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>{o.status}</Badge>{(o.applications_count || 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => onNavigateToApplications?.({ offer_id: o.offer_id, offer_title: o.title, offer_number: o.offer_number })}
                    className="inline-flex items-center"
                    data-testid={`offer-applications-${o.offer_id}`}
                    title={`Voir les ${o.applications_count} candidat(s) qui ont postulé`}
                  >
                    <Badge className="rounded-none text-xs bg-[#E6F0F7] text-[#0069B4] hover:bg-[#0069B4] hover:text-white transition-colors cursor-pointer">
                      {o.applications_count} candidature{o.applications_count > 1 ? "s" : ""}
                    </Badge>
                  </button>
                )}{(o.assigned_users?.length || 0) > 0 && <Badge className="rounded-none text-xs bg-[#F0E9F3] text-[#662483]">{o.assigned_users.length} affecté(s)</Badge>}</div>
              <p className="text-xs text-gray-500">
                {o.location} | {o.hours}
                {(o.mission_start || o.mission_end) && (
                  <span className="ml-2 text-gray-600">
                    · Mission{o.mission_start ? ` du ${formatDateFr(o.mission_start)}` : ""}{o.mission_end ? ` au ${formatDateFr(o.mission_end)}` : ""}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openAssign(o)} className="p-2 hover:bg-gray-100 text-[#662483]" title="Affecter des candidats" data-testid={`assign-offer-${o.offer_id}`}><UserPlus className="w-4 h-4" /></button>
              <button onClick={() => handleToggleStatus(o)} className="p-2 hover:bg-gray-100" data-testid={`toggle-status-${o.offer_id}`}>{o.status === "active" ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}</button>
              <button onClick={() => handleDuplicate(o)} className="p-2 hover:bg-gray-100 text-[#662483]" title="Dupliquer cette offre" data-testid={`duplicate-offer-${o.offer_id}`}><Copy className="w-4 h-4" /></button>
              <button onClick={() => handleSaveAsTemplate(o)} className="p-2 hover:bg-gray-100 text-[#0069B4]" title="Enregistrer comme modèle" data-testid={`save-as-template-${o.offer_id}`}><BookmarkPlus className="w-4 h-4" /></button>
              <button onClick={() => openEdit(o)} className="p-2 hover:bg-gray-100 text-[#0069B4]" data-testid={`edit-offer-${o.offer_id}`}><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(o.offer_id)} className="p-2 hover:bg-gray-100 text-red-500" data-testid={`delete-offer-${o.offer_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#3F3F3F]">{editOffer ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-sm font-semibold mb-1 block">Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="offer-form-title" /></div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Agence responsable</Label>
              <Select value={form.agency_id || "__none__"} onValueChange={(v) => setForm({ ...form, agency_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-none" data-testid="offer-form-agency"><SelectValue placeholder="Aucune (utilise l&apos;expéditeur global)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucune agence responsable</SelectItem>
                  {agencies.map((a) => (
                    <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}{a.email ? ` — ${a.email}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-gray-500 mt-1">Tous les emails déclenchés par cette offre (candidature reçue, réponses…) seront envoyés depuis l&apos;adresse de l&apos;agence sélectionnée.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold mb-1 block">Lieu</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-none" data-testid="offer-form-location" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Horaires</Label><Input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="rounded-none" data-testid="offer-form-hours" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold mb-1 block">Salaire</Label><Input value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="rounded-none" data-testid="offer-form-salary" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Profil recherché</Label><Input value={form.profile_needed} onChange={(e) => setForm({ ...form, profile_needed: e.target.value })} className="rounded-none" data-testid="offer-form-profile" /></div>
            </div>
            <div><Label className="text-sm font-semibold mb-1 block">Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none min-h-[120px]" data-testid="offer-form-description" /></div>
            <div><Label className="text-sm font-semibold mb-1 block">Exigences</Label><Textarea value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} className="rounded-none" data-testid="offer-form-requirements" /></div>
            <div><Label className="text-sm font-semibold mb-1 block">Avantages</Label><Textarea value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} className="rounded-none" data-testid="offer-form-benefits" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold mb-1 block">Date début mission</Label><Input type="date" value={form.mission_start} onChange={(e) => setForm({ ...form, mission_start: e.target.value })} className="rounded-none" data-testid="offer-form-mission-start" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Date fin mission</Label><Input type="date" value={form.mission_end} onChange={(e) => setForm({ ...form, mission_end: e.target.value })} className="rounded-none" data-testid="offer-form-mission-end" /></div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Catégories (ciblage emails)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {JOB_PREFS.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox checked={(form.categories || []).includes(key)} onCheckedChange={(checked) => setForm(prev => ({ ...prev, categories: checked ? [...(prev.categories || []), key] : (prev.categories || []).filter(c => c !== key) }))} data-testid={`offer-cat-${key}`} />
                    <label className="text-xs text-gray-700">{label}</label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Les candidats ayant ces préférences et disponibles sur la durée recevront l'email automatiquement.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="offer-form-save">{editOffer ? "Modifier" : "Créer"}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={!!assignOffer} onOpenChange={(v) => !v && setAssignOffer(null)}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">Affecter des candidats — {assignOffer?.offer_number} {assignOffer?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#FAFAFA] p-3 border border-gray-200 text-xs space-y-1">
              <p><strong>Lieu :</strong> {assignOffer?.location || "—"}</p>
              <p><strong>Début mission :</strong> {assignOffer?.mission_start ? formatDateFr(assignOffer.mission_start) : "non renseigné"}</p>
              <p><strong>Horaires :</strong> {assignOffer?.hours || "—"}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 bg-white border border-gray-200 p-2 cursor-pointer">
                <Checkbox checked={optsEmail} onCheckedChange={setOptsEmail} data-testid="assign-opt-email" />
                <Mail className="w-4 h-4 text-[#0069B4]" />
                <span className="text-sm">Envoyer email de confirmation</span>
              </label>
              <label className="flex items-center gap-2 bg-white border border-gray-200 p-2 cursor-pointer">
                <Checkbox checked={optsSms} onCheckedChange={setOptsSms} data-testid="assign-opt-sms" />
                <Smartphone className="w-4 h-4 text-[#662483]" />
                <span className="text-sm">Envoyer SMS opérationnel</span>
              </label>
            </div>

            {optsSms && (
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Message SMS personnalisé (optionnel — sinon généré automatiquement)</Label>
                <Textarea value={customSms} onChange={(e) => setCustomSms(e.target.value)} placeholder="Laisser vide pour générer auto" maxLength={480} className="rounded-none text-sm min-h-[60px]" data-testid="assign-custom-sms" />
                <p className="text-[10px] text-gray-400 mt-1">⚠️ Les candidats sans consentement SMS opérationnel ou sans numéro seront ignorés (l'email part quand même).</p>
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold mb-2 block">Sélection des candidats ({selectedUsers.size} sélectionné(s))</Label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input value={searchCand} onChange={(e) => setSearchCand(e.target.value)} placeholder="Rechercher par nom, email, ville..." className="rounded-none pl-10 text-sm h-9" data-testid="assign-search" />
              </div>
              <div className="max-h-72 overflow-y-auto border border-gray-200 divide-y divide-gray-100">
                {candidates
                  .filter(c => {
                    if (!searchCand) return true;
                    const s = searchCand.toLowerCase();
                    return (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.city || "").toLowerCase().includes(s);
                  })
                  .slice(0, 200)
                  .map(c => {
                    const checked = selectedUsers.has(c.user_id);
                    return (
                      <label key={c.user_id} className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-[#FAFAFA] ${checked ? "bg-[#F0E9F3]" : ""}`} data-testid={`assign-candidate-${c.user_id}`}>
                        <Checkbox checked={checked} onCheckedChange={(v) => {
                          const s = new Set(selectedUsers);
                          if (v) s.add(c.user_id); else s.delete(c.user_id);
                          setSelectedUsers(s);
                        }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#3F3F3F] truncate">{c.name || "(sans nom)"}</p>
                          <p className="text-[11px] text-gray-500 truncate">{c.email} {c.phone && `• ${c.phone}`} {c.city && `• ${c.city}`}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {c.role === "employee" && <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none text-[10px]">Salarié</Badge>}
                          {c.sms_operational_consent && <Badge className="bg-green-100 text-green-700 rounded-none text-[10px]">SMS OK</Badge>}
                        </div>
                      </label>
                    );
                  })}
                {candidates.length === 0 && <p className="text-center py-6 text-sm text-gray-500">Chargement...</p>}
              </div>
            </div>

            <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-2">
              <Button onClick={handleAssign} disabled={assigning || selectedUsers.size === 0} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="assign-confirm-btn">
                <UserPlus className="w-4 h-4 mr-2" /> {assigning ? "Envoi en cours..." : `Affecter ${selectedUsers.size} candidat(s) + notifier`}
              </Button>
              <Button variant="outline" onClick={() => setAssignOffer(null)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F] flex items-center gap-2"><FileStack className="w-5 h-5 text-[#662483]" /> Modèles d'offres</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <p className="text-xs text-gray-500">Cliquez sur un modèle pour créer une nouvelle offre à partir de celui-ci (status « inactive » par défaut, à réviser).</p>
            {templates.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">Aucun modèle. Cliquez sur l'icône <BookmarkPlus className="w-3 h-3 inline" /> d'une offre pour la sauvegarder en modèle.</div>
            ) : (
              <div className="divide-y divide-gray-100 border border-gray-200">
                {templates.map((t) => (
                  <div key={t.offer_id} className="p-3 flex items-center justify-between gap-3 hover:bg-[#FAFAFA]" data-testid={`template-${t.offer_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#3F3F3F] truncate">{t.template_name || t.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{t.title} • {t.location || "—"} • {t.hours || "—"}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" onClick={() => handleCreateFromTemplate(t)} className="bg-[#662483] text-white rounded-none text-xs h-8" data-testid={`use-template-${t.offer_id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Utiliser
                      </Button>
                      <button onClick={() => handleDeleteTemplate(t)} className="p-2 hover:bg-gray-100 text-red-500" title="Supprimer ce modèle" data-testid={`delete-template-${t.offer_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
